import { PianoEffectsSettings } from '../types';

export const DEFAULT_EFFECTS_SETTINGS: PianoEffectsSettings = {
  reverb: {
    enabled: true,
    mix: 22,
    roomSize: 1.8,
    damping: 'natural',
  },
  delay: {
    enabled: false,
    time: 0.32, // 320ms (croche / 8th note at 94 BPM)
    feedback: 35, // 35% répétitions
    mix: 20, // 20% dosage mix
  },
  tone: {
    enabled: true,
    brightness: 0, // -50 (feutré/mellow) à +50 (brillant/bright)
  },
  decayTime: 1.6, // secondes de release
};

export class EffectsChain {
  public readonly input: GainNode;
  public readonly output: GainNode;

  private ctx: AudioContext;
  private settings: PianoEffectsSettings = { ...DEFAULT_EFFECTS_SETTINGS };
  private listeners = new Set<(settings: PianoEffectsSettings) => void>();

  // Tone Filter Nodes
  private toneFilter: BiquadFilterNode;

  // Reverb Nodes
  private convolver: ConvolverNode;
  private reverbFilter: BiquadFilterNode;
  private dryGain: GainNode;
  private wetGain: GainNode;

  // Delay Nodes
  private delayNode: DelayNode;
  private delayFeedbackGain: GainNode;
  private delayFilter: BiquadFilterNode;
  private delayWetGain: GainNode;

  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;

    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.output.connect(destination);

    // 1. Tone / Brightness (High Shelf at 3200Hz)
    this.toneFilter = ctx.createBiquadFilter();
    this.toneFilter.type = 'highshelf';
    this.toneFilter.frequency.setValueAtTime(3200, ctx.currentTime);
    this.toneFilter.gain.setValueAtTime(0, ctx.currentTime);

    // 2. Reverb Chain
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    this.reverbFilter = ctx.createBiquadFilter();
    this.reverbFilter.type = 'lowpass';
    this.reverbFilter.frequency.setValueAtTime(4600, ctx.currentTime);
    this.reverbFilter.Q.setValueAtTime(0.7, ctx.currentTime);

    this.convolver = ctx.createConvolver();
    this.convolver.buffer = this.generateImpulseResponse(
      this.settings.reverb.roomSize,
      this.settings.reverb.damping
    );

    // 3. Delay Chain (Warm analog/tape style delay with filter)
    this.delayNode = ctx.createDelay(2.0);
    this.delayNode.delayTime.setValueAtTime(this.settings.delay.time, ctx.currentTime);

    this.delayFeedbackGain = ctx.createGain();
    const fb = Math.min(0.80, this.settings.delay.feedback / 100);
    this.delayFeedbackGain.gain.setValueAtTime(fb, ctx.currentTime);

    this.delayFilter = ctx.createBiquadFilter();
    this.delayFilter.type = 'lowpass';
    this.delayFilter.frequency.setValueAtTime(3200, ctx.currentTime); // gentle warm high-cut on echoes
    this.delayFilter.Q.setValueAtTime(0.7, ctx.currentTime);

    this.delayWetGain = ctx.createGain();
    this.delayWetGain.gain.setValueAtTime(0.0001, ctx.currentTime);

    // Routing:
    // Input -> ToneFilter -> DryGain -> Output
    //                      -> Convolver -> ReverbFilter -> WetGain -> Output
    //                      -> DelayNode -> DelayFilter -> DelayWetGain -> Output
    //                                         `--> DelayFeedbackGain -> DelayNode
    this.input.connect(this.toneFilter);

    // Dry
    this.toneFilter.connect(this.dryGain);
    this.dryGain.connect(this.output);

    // Reverb
    this.toneFilter.connect(this.convolver);
    this.convolver.connect(this.reverbFilter);
    this.reverbFilter.connect(this.wetGain);
    this.wetGain.connect(this.output);

    // Delay
    this.toneFilter.connect(this.delayNode);
    this.delayNode.connect(this.delayFilter);
    this.delayFilter.connect(this.delayWetGain);
    this.delayWetGain.connect(this.output);

    this.delayFilter.connect(this.delayFeedbackGain);
    this.delayFeedbackGain.connect(this.delayNode);

    this.applyReverbGains();
    this.applyDelayGains();
  }

  public getSettings(): PianoEffectsSettings {
    return JSON.parse(JSON.stringify(this.settings));
  }

  public subscribe(listener: (settings: PianoEffectsSettings) => void): () => void {
    this.listeners.add(listener);
    listener(this.getSettings());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    const current = this.getSettings();
    for (const listener of this.listeners) {
      try {
        listener(current);
      } catch {
        // ignore
      }
    }
  }

  public updateSettings(partial: Partial<PianoEffectsSettings>): void {
    const prevRoomSize = this.settings.reverb.roomSize;
    const prevDamping = this.settings.reverb.damping;

    if (partial.reverb) {
      this.settings.reverb = { ...this.settings.reverb, ...partial.reverb };
    }
    if (partial.delay) {
      this.settings.delay = { ...this.settings.delay, ...partial.delay };
      const clampedTime = Math.max(0.04, Math.min(1.5, this.settings.delay.time));
      this.delayNode.delayTime.setTargetAtTime(clampedTime, this.ctx.currentTime, 0.03);
      const clampedFb = Math.max(0, Math.min(0.82, this.settings.delay.feedback / 100));
      this.delayFeedbackGain.gain.setTargetAtTime(clampedFb, this.ctx.currentTime, 0.03);
      this.applyDelayGains();
    }
    if (partial.tone) {
      this.settings.tone = { ...this.settings.tone, ...partial.tone };
    }
    if (partial.decayTime !== undefined) {
      this.settings.decayTime = Math.max(0.2, Math.min(3.0, partial.decayTime));
    }

    // Apply Tone
    if (!this.settings.tone.enabled) {
      this.toneFilter.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
    } else {
      // Maps -50..+50 to -9dB..+7dB
      const dbGain = (this.settings.tone.brightness / 50) * 8;
      this.toneFilter.gain.setTargetAtTime(dbGain, this.ctx.currentTime, 0.05);
    }

    // Apply Reverb Filtering based on damping mode
    let cutoff = 4600;
    if (this.settings.reverb.damping === 'warm') cutoff = 3000;
    if (this.settings.reverb.damping === 'bright') cutoff = 8000;
    this.reverbFilter.frequency.setTargetAtTime(cutoff, this.ctx.currentTime, 0.05);

    // Apply Reverb Gains
    this.applyReverbGains();

    // Recompute IR if room size or damping changed
    if (
      this.settings.reverb.roomSize !== prevRoomSize ||
      this.settings.reverb.damping !== prevDamping
    ) {
      try {
        this.convolver.buffer = this.generateImpulseResponse(
          this.settings.reverb.roomSize,
          this.settings.reverb.damping
        );
      } catch (err) {
        console.warn('Convolver IR update warning:', err);
      }
    }

    this.notify();
  }

  private applyReverbGains(): void {
    const now = this.ctx.currentTime;
    if (!this.settings.reverb.enabled) {
      // Bypass: 100% dry, 0% wet
      this.dryGain.gain.setTargetAtTime(1.0, now, 0.03);
      this.wetGain.gain.setTargetAtTime(0.0001, now, 0.03);
    } else {
      const wetRatio = Math.max(0, Math.min(1, this.settings.reverb.mix / 100));
      // Equal-power like curve
      const dryVal = Math.max(0.2, Math.cos((wetRatio * Math.PI) / 2));
      const wetVal = Math.sin((wetRatio * Math.PI) / 2) * 0.85;

      this.dryGain.gain.setTargetAtTime(dryVal, now, 0.03);
      this.wetGain.gain.setTargetAtTime(wetVal, now, 0.03);
    }
  }

  private applyDelayGains(): void {
    const now = this.ctx.currentTime;
    if (!this.settings.delay.enabled) {
      this.delayWetGain.gain.setTargetAtTime(0.0001, now, 0.03);
    } else {
      const wetRatio = Math.max(0, Math.min(1, this.settings.delay.mix / 100));
      this.delayWetGain.gain.setTargetAtTime(wetRatio * 0.85, now, 0.03);
    }
  }

  private generateImpulseResponse(
    seconds: number,
    damping: 'warm' | 'natural' | 'bright'
  ): AudioBuffer {
    const rate = this.ctx.sampleRate;
    const dur = Math.max(0.4, Math.min(4.0, seconds));
    const length = Math.floor(rate * dur);
    const impulse = this.ctx.createBuffer(2, length, rate);
    const left = impulse.getChannelData(0);
    const right = impulse.getChannelData(1);

    const preDelaySamples = Math.floor(rate * 0.012);
    const decayPower = damping === 'warm' ? 2.6 : damping === 'bright' ? 1.7 : 2.1;

    for (let i = 0; i < length; i++) {
      if (i < preDelaySamples) {
        left[i] = 0;
        right[i] = 0;
        continue;
      }
      const progress = (i - preDelaySamples) / (length - preDelaySamples);
      const envelope = Math.pow(1 - progress, decayPower);

      // Stereo decorrelated random values
      left[i] = (Math.random() * 2 - 1) * envelope;
      right[i] = (Math.random() * 2 - 1) * envelope;
    }

    return impulse;
  }
}
